import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, BedDouble, Ruler, ArrowUpRight, CalendarCheck, PawPrint, BadgePercent, Tent, Tag } from "lucide-react";
import { Reveal } from "../components/Chapter";
import { ACCOMMODATIONS, ACCOMMODATION_CATEGORIES, BOOKING_URL, PRICE_GUIDE } from "../lib/data";

const CATEGORY_STYLE = {
  "mobil-home": "bg-pine text-sand",
  insolite: "bg-terra text-sand",
  emplacement: "bg-brun text-ink",
};
const CATEGORY_LABEL = {
  "mobil-home": "Mobil-home",
  insolite: "Insolite",
  emplacement: "Emplacement",
};

const INFOS = [
  {
    icon: CalendarCheck,
    title: "Arrivées flexibles",
    text: "Jours d'arrivée libres hors saison ; samedi, dimanche ou mercredi de début juillet à fin août.",
  },
  {
    icon: PawPrint,
    title: "Animaux bienvenus",
    text: "Vos compagnons à quatre pattes sont acceptés (avec supplément) dans nos hébergements.",
  },
  {
    icon: BadgePercent,
    title: "Carte ACSI",
    text: "Profitez de promotions sur votre location hors saison grâce à la carte ACSI.",
  },
];

export default function HebergementsPage() {
  const [filter, setFilter] = useState("tous");
  const list = filter === "tous" ? ACCOMMODATIONS : ACCOMMODATIONS.filter((a) => a.category === filter);

  return (
    <main data-testid="page-hebergements" className="grain">
      <section className="relative pt-40 pb-16 px-6 sm:px-10 lg:px-16 bg-cream overflow-hidden">
        <Tent className="absolute -right-10 -top-6 w-64 h-64 text-pine/5 rotate-12" />
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-body text-xs font-bold tracking-[0.25em] uppercase text-terra">
              Nos hébergements
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-3xl">
              Abri sous les chênes,<br />
              <span className="text-pine">le confort en plus.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-ink/65 text-base lg:text-lg leading-relaxed">
              Mobil-homes climatisés, lodges safari et hébergements insolites, emplacements
              spacieux pour tente, caravane ou camping-car : au bord du lac, avec piscine couverte
              et animaux acceptés, trouvez la formule qui vous ressemble — de la nuit dernière
              minute à la semaine tout compris.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-2" data-testid="hebergements-filters">
              {ACCOMMODATION_CATEGORIES.map(([key, label]) => {
                const count = key === "tous" ? ACCOMMODATIONS.length : ACCOMMODATIONS.filter((a) => a.category === key).length;
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    data-testid={`filter-${key}`}
                    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                      filter === key
                        ? "bg-pine text-sand shadow-lg shadow-pine/25"
                        : "bg-sand text-ink/60 border border-ink/10 hover:border-pine/40 hover:text-ink"
                    }`}
                  >
                    {label} <span className={filter === key ? "text-sand/60" : "text-ink/35"}>({count})</span>
                  </button>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 bg-cream">
        <div className="max-w-7xl mx-auto">
          <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            <AnimatePresence mode="popLayout">
              {list.map((a, i) => (
                <motion.article
                  layout
                  key={a.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45, delay: Math.min(i * 0.04, 0.3), ease: [0.22, 1, 0.36, 1] }}
                  className="group flex flex-col rounded-[1.75rem] bg-sand border border-ink/10 overflow-hidden shadow-md shadow-ink/5 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-pine/15"
                  data-testid={`accommodation-card-${i}`}
                >
                  <div className="relative h-60 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className={`absolute top-4 left-4 rounded-full px-3.5 py-1.5 text-[11px] font-bold tracking-[0.14em] uppercase shadow ${CATEGORY_STYLE[a.category]}`}>
                      {CATEGORY_LABEL[a.category]}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1 p-6">
                    <h3 className="font-display font-bold text-2xl text-ink leading-tight">{a.name}</h3>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink/55 font-medium">
                      <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-pine" /> {a.people}</span>
                      {a.rooms && <span className="inline-flex items-center gap-1.5"><BedDouble size={14} className="text-pine" /> {a.rooms}</span>}
                      <span className="inline-flex items-center gap-1.5"><Ruler size={14} className="text-pine" /> {a.size}</span>
                    </div>
                    <p className="mt-3 text-sm text-ink/60 leading-relaxed">{a.desc}</p>
                    {PRICE_GUIDE[a.name] && (
                      <div className="mt-4 flex items-center gap-2.5 rounded-xl bg-cream/80 border border-brun/30 px-4 py-2.5" data-testid={`accommodation-price-${i}`}>
                        <Tag size={14} className="text-terra shrink-0" />
                        <span className="text-xs text-ink/65 leading-snug">
                          Basse saison dès <b className="text-pine">{PRICE_GUIDE[a.name].low} €</b>
                          <span className="mx-1.5 text-ink/30">·</span>
                          Haute saison dès <b className="text-terra">{PRICE_GUIDE[a.name].high} €</b>
                          <span className="text-ink/40"> /nuit</span>
                        </span>
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {a.features.map((f) => (
                        <span key={f} className="rounded-full bg-cream border border-ink/10 px-3 py-1 text-xs font-medium text-ink/60">
                          {f}
                        </span>
                      ))}
                    </div>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`accommodation-book-${i}`}
                      className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-terra text-sand text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-pine group-hover:shadow-lg group-hover:shadow-terra/25"
                    >
                      Voir les disponibilités <ArrowUpRight size={15} />
                    </a>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16 pb-6 bg-cream">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            {
              title: "Camping-car et piscine en Aveyron",
              text: "Les camping-cars sont les bienvenus : emplacements spacieux avec électricité en option et sanitaires rénovés à deux pas. Et oui, la piscine couverte chauffée vous attend aussi après la route.",
            },
            {
              title: "Mai, le pont de l'Ascension et les promotions",
              text: "Mai est un secret bien gardé : nature en fleurs, calme absolu et tarifs doux. Profitez du pont de l'Ascension, de nos promotions hors saison et des offres dernière minute pour un week-end au bord du lac à petit prix.",
            },
            {
              title: "Pêche au lac de Bannac, à 50 m",
              text: "Le lac de Bannac (22 hectares) est à 50 m du camping : l'accès est gratuit pour les campeurs munis d'une carte de pêche, en no-kill uniquement. Carpistes et pêcheurs au coup, vos aubes sont ici.",
            },
          ].map((b, i) => (
            <Reveal key={b.title} delay={0.06 * i}>
              <div className="h-full rounded-2xl border border-ink/10 bg-sand/70 p-6" data-testid={`hebergements-seo-${i}`}>
                <h3 className="font-display font-bold text-lg text-pine">{b.title}</h3>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16 pb-28 bg-cream">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-5">
          {INFOS.map((info, i) => (
            <Reveal key={info.title} delay={0.08 * i}>
              <div
                className="h-full rounded-2xl border border-ink/10 bg-sand p-6 flex items-start gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-pine/10"
                data-testid={`hebergements-info-${i}`}
              >
                <span className="grid place-items-center w-11 h-11 shrink-0 rounded-full bg-pine text-sand">
                  <info.icon size={19} />
                </span>
                <div>
                  <div className="font-display font-bold text-lg text-ink">{info.title}</div>
                  <p className="mt-1 text-sm text-ink/60 leading-relaxed">{info.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-10 text-center font-hand text-3xl text-terra -rotate-1">
            une question sur un hébergement ? appelez-nous, on adore conseiller !
          </p>
        </Reveal>
      </section>
    </main>
  );
}
